import { FC, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  ActionBtnStyled,
  ControlsWrapStyled,
  FlexContainerMessageStyled,
  FormElementWrapStyled,
  FormStyled,
  TypographyTitleStyled,
  getPopupContentStyle,
} from './ChangeTicketClaimStatusPopup.styled';
import useChangeClaimStatusPopup from './hooks/useChangeTicketClaimStatusPopup';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import { FormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { NoopT } from 'declarations/types/common';

type ChangeTicketClaimStatusPopupPropsT = {
  ticketId: string;
  claimStatusId: number;
  isOpen: boolean;
  onClose: NoopT;
};

export type ChangeTicketClaimStatusFormValuesT = {
  ticketId: string;
  claimStatusId: number;
};

const ChangeTicketClaimStatusPopup: FC<ChangeTicketClaimStatusPopupPropsT> = ({
  ticketId,
  claimStatusId,
  isOpen,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const {
    isChangingClaimStatus,
    isClaimStatusChanged,
    isClaimStatusChangeError,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions,
    changeTicketClaimStatusErrorMessage,
    changeTicketClaimStatusValidationErrorMessage,
    showPopupContent,
    handleChangeTicketClaimStatus,
    handleClosePopup,
  } = useChangeClaimStatusPopup();

  const fromMethods = useForm<ChangeTicketClaimStatusFormValuesT>({
    defaultValues: {
      ticketId: ticketId,
      claimStatusId: claimStatusId,
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, setValue } = fromMethods;

  useEffect(() => {
    if (isOpen) {
      setValue('ticketId', ticketId);
      setValue('claimStatusId', claimStatusId);
    }
  }, [isOpen, ticketId, claimStatusId, setValue]);

  const selectedClaimStatusId = useWatch<ChangeTicketClaimStatusFormValuesT, 'claimStatusId'>({
    control,
    name: 'claimStatusId',
  });

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn={isClaimStatusChanged || isClaimStatusChangeError}
      popupId={ScrollLockIdE.ChangeTicketClaimStatusPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <>
          <TypographyTitleStyled variant="h2" color="dark" align="center">
            Change claim status
          </TypographyTitleStyled>
          <FormProvider {...fromMethods}>
            <FormStyled role="form" onSubmit={handleSubmit(handleChangeTicketClaimStatus)}>
              <FormElementWrapStyled>
                <FormSelect
                  isRequired
                  label="Choose Claim status"
                  name="claimStatusId"
                  placeholder="Claim statuses"
                  options={claimStatusesOptions}
                  isLoading={isLoadingClaimStatusesOptions}
                />
              </FormElementWrapStyled>
              <ControlsWrapStyled justify={isMobile ? 'center' : 'flex-end'}>
                <ActionBtnStyled
                  type="button"
                  variant="outline"
                  size="xl"
                  onClick={handleClosePopup(onClose)}
                >
                  Cancel
                </ActionBtnStyled>
                <ActionBtnStyled
                  variant="contained"
                  size="xl"
                  isLoading={isChangingClaimStatus}
                  disabled={selectedClaimStatusId === claimStatusId}
                >
                  Save
                </ActionBtnStyled>
              </ControlsWrapStyled>
            </FormStyled>
          </FormProvider>
        </>
      )}

      {isClaimStatusChanged && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Claim status was changed successfully" />
        </FlexContainerMessageStyled>
      )}

      {isClaimStatusChangeError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={
              changeTicketClaimStatusErrorMessage || changeTicketClaimStatusValidationErrorMessage
            }
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default ChangeTicketClaimStatusPopup;
